import React, { FunctionComponent, useCallback } from 'react';
import { Box } from '@mui/material';

import { MaterialSelection, MaterialSettings } from '@paragon/contract-library';
import { hasCanUsePlatesPermission } from '@paragon/login-tools';
import { getLoggedInUsersOrganizationMaterialSet, MaterialListSelect } from '@paragon/material-lists';

import { getUniqueLumbersInLumberList } from '../../../../../store/selectors/getUniqueLumbersInLumberList';
import { useAppSelector } from '../../../../hooks';
import ComponentThicknessProperty from './ComponentThicknessProperty';
import { restrictMaterialSettingsLumbers } from './materialSettingsValidation';

interface MaterialSelectionControlsProps {
  readonly materialSelection: MaterialSelection;
  readonly onMaterialSelectionChange: (materialSelection: MaterialSelection) => void;

  readonly featurePatternMaterialSettings?: MaterialSettings;
  readonly profileMaterialSettings?: MaterialSettings;
  readonly onFeaturePatternMaterialSettingsChange?: (settings: MaterialSettings) => void;
  readonly onProfileMaterialSettingsChange?: (settings: MaterialSettings) => void;

  readonly componentThickness?: number;
  readonly onThicknessChange?: (thickness: number) => void;

  readonly showThicknessControl?: boolean;
  readonly disabled?: boolean;
}

export const MaterialSelectionControls: FunctionComponent<MaterialSelectionControlsProps> = ({
  materialSelection,
  onMaterialSelectionChange,
  featurePatternMaterialSettings,
  profileMaterialSettings,
  onFeaturePatternMaterialSettingsChange,
  onProfileMaterialSettingsChange,
  componentThickness,
  onThicknessChange,
  showThicknessControl,
  disabled,
}) => {
  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);
  const materialSet = useAppSelector(getLoggedInUsersOrganizationMaterialSet);

  const updateAndRestrictLumbers = useCallback(
    (newLumberListGuid: string, newThickness?: number | undefined) => {
      const lumberListChanged = materialSelection.lumberListGuid !== newLumberListGuid;
      const thicknessChanged = newThickness != null && componentThickness !== newThickness;

      if (lumberListChanged) {
        onMaterialSelectionChange({
          ...materialSelection,
          lumberListGuid: newLumberListGuid,
        });
      }

      if (thicknessChanged && onThicknessChange) {
        onThicknessChange(newThickness);
      }

      const newLumberListLumbers = getUniqueLumbersInLumberList(materialSet, newLumberListGuid);

      if (featurePatternMaterialSettings && onFeaturePatternMaterialSettingsChange) {
        const newFeaturePatternMaterialSettings = restrictMaterialSettingsLumbers(
          featurePatternMaterialSettings,
          newLumberListLumbers,
          newThickness ?? componentThickness,
        );

        if (newFeaturePatternMaterialSettings !== featurePatternMaterialSettings) {
          onFeaturePatternMaterialSettingsChange(newFeaturePatternMaterialSettings);
        }
      }

      if (profileMaterialSettings && onProfileMaterialSettingsChange) {
        const newProfileMaterialSettings = restrictMaterialSettingsLumbers(
          profileMaterialSettings,
          newLumberListLumbers,
          1.5, // Hardcoded for now
        );

        if (newProfileMaterialSettings !== profileMaterialSettings) {
          onProfileMaterialSettingsChange(newProfileMaterialSettings);
        }
      }
    },
    [
      materialSelection,
      componentThickness,
      featurePatternMaterialSettings,
      profileMaterialSettings,
      onMaterialSelectionChange,
      onThicknessChange,
      onFeaturePatternMaterialSettingsChange,
      onProfileMaterialSettingsChange,
      materialSet,
    ],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', m: 2 }}>
        <MaterialListSelect
          sx={{ m: 1 }}
          type="lumber"
          disabled={disabled}
          selectedGuid={materialSelection.lumberListGuid}
          onEdit={updateAndRestrictLumbers}
        />
        {canUsePlates && (
          <MaterialListSelect
            sx={{ m: 1 }}
            type="plate"
            disabled={disabled}
            selectedGuid={materialSelection.plateListGuid}
            onEdit={(guid) =>
              onMaterialSelectionChange({
                ...materialSelection,
                plateListGuid: guid,
              })
            }
          />
        )}
        <MaterialListSelect
          sx={{ m: 1 }}
          type="hanger"
          disabled={disabled}
          selectedGuid={materialSelection.hangerListGuid}
          onEdit={(guid) =>
            onMaterialSelectionChange({
              ...materialSelection,
              hangerListGuid: guid,
            })
          }
        />
      </Box>

      {showThicknessControl && componentThickness != null && onThicknessChange && (
        <ComponentThicknessProperty
          thickness={componentThickness}
          setFeaturePatternTrussThickness={(thickness) =>
            updateAndRestrictLumbers(materialSelection.lumberListGuid, thickness)
          }
        />
      )}
    </Box>
  );
};
